import { createSelector, createStructuredSelector } from 'reselect';
import { initialState } from './reducer';
import { initialState as avcInitialState } from '../AvailabilityCheck/reducer';
import { RootState } from './types';
import { APP_NAMESPACE, AVC_NAMESPACE } from './constants';
import { HomesPassedType } from '../../enums/product';
import { BEST_BANDWIDTH, INSTALLATION_FEE_REGEX } from '../../constants';

const selectState = (state: RootState) => state[APP_NAMESPACE] || initialState;
const selectAvcState = (state: RootState) => state[AVC_NAMESPACE] || avcInitialState;

export const selectAppState = createSelector(selectState, state => state);
export const selectLoading = createSelector(selectState, state => state.isLoading);
export const selectAddress = createSelector(selectState, state => state.address);
export const selectTariff = createSelector(selectState, state => state.selectedTariff.tariff);
export const selectTariffs = createSelector(selectState, state => state.tariffs);
export const selectSelected = createSelector(selectState, state => state.selectedTariff);
export const selectAvcResult = createSelector(selectState, state => state.avcResult);
export const selectBackendDown = createSelector(selectState, state => Boolean(state.error));
export const selectLoaded = createSelector(selectState, state => state.isOverlayLoaded);
export const selectIsOverlayOpen = createSelector(selectState, state => state.isOverlayOpen);
export const selectActiveOverlay = createSelector(selectState, state => state.activeOverlay);
export const selectContentEnabled = createSelector(selectState,
    ({ avcResult, tariffs, homesPassed }) => {
        if (!avcResult) {
            return true;
        }

        if (!avcResult.availability.fiber) {
            return false;
        }

        if (avcResult && homesPassed === HomesPassedType.BHS) {
            return false;
        }

        const available = tariffs.filter(t => avcResult.metacodes.includes(t.id));

        return Boolean(available.length);
    }
);
export const selectHomesPassed = createSelector(selectState, state => state.homesPassed);
export const selectInstallationFee = createSelector(selectState, ({ selectedTariff }) => {
    if (!selectContentEnabled) {
        return;
    }

    const installationFeeOptions = selectedTariff.tariff?.options?.filter(
        item => INSTALLATION_FEE_REGEX.test(item.id)
    );

    if (!installationFeeOptions?.length) {
        return;
    }

    // installation fee options are coming back from Jarvis
    // in increasing order and we need the highest
    return installationFeeOptions[installationFeeOptions.length - 1];
});
export const selectRecommendedTariff = createSelector(selectTariffs, tariffs =>
    tariffs.find(tariff => +tariff.download === BEST_BANDWIDTH)
);
export const selectAccessSupplier = createSelector(selectAvcState, state => state.accessSupplier);
export const selectAvailabilityIndicator = createSelector(
    selectAvcState,
    state => state.availabilityIndicator
);

export const selectors = createStructuredSelector({
    appState: selectAppState,
    isLoading: selectLoading,
    address: selectAddress,
    selected: selectSelected,
    avcResult: selectAvcResult,
    isBackendDown: selectBackendDown,
    selectedTariff: selectTariff,
    recommendedTariff: selectRecommendedTariff,
    tariffs: selectTariffs,
    activeOverlay: selectActiveOverlay,
    isOverlayLoaded: selectLoaded,
    isOverlayOpen: selectIsOverlayOpen,
    isContentEnabled: selectContentEnabled,
    homesPassed: selectHomesPassed,
    installationFee: selectInstallationFee,
    accessSupplier: selectAccessSupplier,
    availabilityIndicator: selectAvailabilityIndicator,
});
